import { countryCodesDict } from "CONSTANTS";
import { LoadingLineItem, LoadingStatus } from "api/wms/models";
import { Button } from "components/miloDesignSystem/atoms/button";
import { IconButton } from "components/miloDesignSystem/atoms/iconButton";
import { MdiCheck } from "components/miloDesignSystem/atoms/icons/MdiCheck";
import { MdiQrCode } from "components/miloDesignSystem/atoms/icons/MdiQrCode";
import { Tag } from "components/miloDesignSystem/atoms/tag";
import { Typography } from "components/miloDesignSystem/atoms/typography";
import { ListAvatar } from "pages/wms/shared/components/ListAvatar";
import { cx, dateFns } from "utilities";
import { useCreateTableColumns } from "utilities/tableColumnsUtilities/createTableColumns/createTableColumns";
import styles from "../../RightPanel.module.css";
import { useDownloadLoadingLineItemPdf } from "../../hooks/useDownloadLoadingLineItemPdf";

export const useLineItemsColumns = () => {
  const handleDownloadLoadingLineItemPdf = useDownloadLoadingLineItemPdf();

  return useCreateTableColumns<LoadingLineItem>(({ columnHelper }) => {
    return [
      columnHelper.accessor(row => row, {
        header: "sygnatura",
        size: 150,
        cell: info => {
          const loading = info.getValue();
          return (
            <Typography
              className={cx({
                [styles.cancelledSignature]: loading.isCancelled,
              })}
              fontSize="12"
              fontWeight="700"
            >
              {Boolean(loading.signature.length) ? `${loading.signature}` : "---"}
            </Typography>
          );
        },
      }),
      columnHelper.accessor(row => row.status, {
        header: "",
        id: "status",
        size: 45,
        cell: info => {
          const status = info.getValue();
          if (status === LoadingStatus.FINISHED) {
            return <Tag startIcon={MdiCheck} label="OK" variant="success" />;
          }
          if (status === LoadingStatus.IN_PROGRESS) {
            return <Tag label="trwa" variant="warning" />;
          }
          return null;
        },
      }),
      columnHelper.text(
        row =>
          row.buyer.firstName || row.buyer.lastName
            ? `${row.buyer.firstName} ${row.buyer.lastName}`
            : null,
        {
          header: "klient",
          size: 105,
        },
      ),
      columnHelper.accessor(row => row.loadingPackagesCounters, {
        header: "towar",
        size: 40,
        cell: info => {
          const loadingPackagesCounters = info.getValue();
          return (
            <div className="d-flex align-items-center">
              <Typography color="success500" fontSize="14" fontWeight="700">
                {loadingPackagesCounters.loaded}
              </Typography>
              <Typography color="neutralBlack48" fontSize="14" fontWeight="700">
                /{loadingPackagesCounters.total}
              </Typography>
            </div>
          );
        },
      }),
      columnHelper.accessor(row => row.address, {
        header: "kraj wysyłki",
        size: 65,
        cell: info => {
          const address = info.getValue();
          return (
            <div className="d-flex align-items-center gap-1">
              {address.countryCode && (
                <img alt="kraj" src={countryCodesDict[address.countryCode]} />
              )}
              <Typography fontSize="10" fontWeight="700">
                {address.countryCode ?? "---"}
              </Typography>
            </div>
          );
        },
      }),
      columnHelper.accessor(row => row.loadedAt, {
        id: "loadedAt",
        header: () => (
          <Typography
            className="text-right w-100"
            color="neutralBlack48"
            fontSize="12"
            fontWeight="400"
          >
            załadowano
          </Typography>
        ),
        size: 100,
        cell: info => {
          return (
            <Typography className="text-right w-100" fontSize="12" fontWeight="700">
              {info.getValue()
                ? dateFns.formatRelative(new Date(info.getValue()), "dd.MM.yyyy, H:mm")
                : "---"}
            </Typography>
          );
        },
      }),
      columnHelper.accessor(row => row.collaborators, {
        header: "kto?",
        size: 45,
        cell: info => {
          const collaborators = info.getValue();
          return <ListAvatar collaborators={collaborators} />;
        },
      }),
      columnHelper.accessor(row => row, {
        header: "",
        id: "labels",
        size: 90,
        cell: info => {
          const loadingLineItem = info.getValue();
          return (
            <div className="d-flex align-items-center gap-2">
              <IconButton
                icon={<MdiQrCode size="16" />}
                onClick={event => {
                  event.stopPropagation();
                  handleDownloadLoadingLineItemPdf(
                    loadingLineItem.id,
                    loadingLineItem.loading,
                    loadingLineItem.signature,
                  );
                }}
                variant="whiteT"
              />
              {loadingLineItem.isCancelled ? (
                <Button
                  onClick={event => event.stopPropagation()}
                  size="small"
                  variant="transparent"
                >
                  Przywróć
                </Button>
              ) : (
                <Button
                  onClick={event => event.stopPropagation()}
                  size="small"
                  variant="transparent"
                >
                  Anuluj
                </Button>
              )}
            </div>
          );
        },
      }),
    ];
  });
};
